import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../services";
import { LoadingScreenService } from "../shared/loading-screen.service";
import { GridViewBaseComponent } from "../utility/grid-view-base.component";
import { SharedDataService } from "../services/sharedDataService";
import { extendDefaultOptions, agDateColumnFilter } from "../utility/gridHelper";
import { PreprocessDateTime } from "../utility/view-field-mapping";
import { LinkCellComponent } from "../shared/grid/link-cell.component";
import { DemandItem } from "../generated-models/DemandItem";
import { DemandItemExpanded } from "../generated-models/DemandItemExpanded";

/** Logical-key lineage rendered in the summary panel of the demand-item-details page. */
interface DemandItemLineage {
  ServiceTreeId: string;
  Type: string;
  Name: string;
  Region: string;
  Resource: string;
}

/**
 *  Details page for a DemandItem — renders the lineage summary, every historical version,
 *  and the related DemandItemExpanded rows across those versions.
 *
 *  Route contract: `/capacity/demand-item/:id` (redirects to logical-key form) or
 *  `/capacity/demand-item/serviceTreeId/:serviceTreeId/type/:type/name/:name/region/:region/resource/:resource?ID=<n>`.
 */
@Component({
  standalone: false,
  templateUrl: "./demand-item-details.component.html",
  styleUrls: ["../styles.scss", "./capacity-blueprint.scss"],
})
export class CapacityBlueprintDemandItemDetailsComponent extends GridViewBaseComponent implements OnInit {
  gridOptions!: GridOptions<DemandItem>;
  gridApi!: GridApi;
  expandedGridOptions!: GridOptions<DemandItemExpanded>;
  expandedGridApi!: GridApi;
  initPageSize = 20;
  keyFilterStats = "filter:capacity-blueprint-demand-item-details";

  demandItemId: number | null = null;
  lineage: DemandItemLineage | null = null;
  rowData: DemandItem[] = [];
  expandedRowData: DemandItemExpanded[] = [];
  isLoading = false;
  errorMessage: string | null = null;

  constructor(
    protected apiService: ApiService,
    protected sharedDataService: SharedDataService,
    private route: ActivatedRoute,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    private cdr: ChangeDetectorRef,
    private titleService: Title,
  ) {
    super(apiService, sharedDataService);
  }

  /** True when the lineage returned no versions — usually an invalid id. */
  get isEmpty(): boolean {
    return !this.isLoading && this.rowData.length === 0 && !this.errorMessage;
  }

  async ngOnInit(): Promise<void> {
    const paramMap = this.route.snapshot.paramMap;
    const queryMap = this.route.snapshot.queryParamMap;

    const idParam = paramMap.get("id");
    let serviceTreeId = paramMap.get("serviceTreeId");
    let type = paramMap.get("type");
    let name = paramMap.get("name");
    let region: string | null = paramMap.get("region");
    let resource: string | null = paramMap.get("resource");

    if (idParam != null) {
      const id = Number(idParam);
      if (Number.isNaN(id)) {
        this.errorMessage = `Invalid Demand Item id: '${idParam}'.`;
        return;
      }
      this.demandItemId = id;

      this.isLoading = true;
      this.loadingService.setLoading(true);
      try {
        const item = await this.apiService.getDemandItem(id).toPromise();
        if (!item) {
          this.errorMessage = `Demand item with id '${id}' was not found.`;
          return;
        }
        const demand = await this.apiService.getDemand(item.DemandId).toPromise();
        if (!demand) {
          this.errorMessage = `Demand with id '${item.DemandId}' was not found.`;
          return;
        }
        serviceTreeId = demand.ServiceTreeId ?? null;
        type = demand.Type ?? null;
        name = demand.BlueprintName ?? null;
        region = demand.RegionName ?? null;
        resource = item.Name ?? null;
        this.lineage = { ServiceTreeId: serviceTreeId, Type: type, Name: name, Region: region, Resource: resource };
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        this.errorMessage = `Failed to load demand item: ${message}`;
        this.notificationService.error(this.errorMessage);
        return;
      } finally {
        this.isLoading = false;
        this.loadingService.setLoading(false);
      }
    } else if (serviceTreeId && type && name && region && resource) {
      const queryId = queryMap.get("ID");
      const parsedQueryId = queryId != null ? Number(queryId) : NaN;
      this.demandItemId = Number.isNaN(parsedQueryId) ? null : parsedQueryId;
    } else {
      this.errorMessage = "Missing route parameters for demand item details.";
      return;
    }

    const titleSuffix = this.demandItemId ?? "";
    this.titleService.setTitle(`Demand Item ${titleSuffix} - Capacity Blueprint`.trim());

    this.isLoading = true;
    this.loadingService.setLoading(true);
    this.expandedGridOptions = this.buildExpandedGridOptions();
    try {
      // History and expanded items are loaded independently so a failure in one
      // does not prevent the other from rendering.
      if (serviceTreeId && type && name && region && resource) {
        try {
          const versions = await this.apiService
            .getDemandItemHistory(serviceTreeId, type, name, region, resource)
            .toPromise();
          if (!versions) {
            this.errorMessage = "Request returned no data.";
          } else {
            this.rowData = versions;
            this.lineage = {
              ServiceTreeId: serviceTreeId,
              Type: type,
              Name: name,
              Region: region,
              Resource: resource,
            };
            this.gridOptions = this.buildGridOptions();
          }
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : String(err);
          this.errorMessage = `Failed to load demand item history: ${message}`;
          this.notificationService.error(this.errorMessage);
        }
      }

      await this.loadExpandedItems();
    } finally {
      this.isLoading = false;
      this.loadingService.setLoading(false);
      this.cdr.detectChanges();
    }
  }

  onGridReady(params: GridReadyEvent) {
    super.onGridReady(params);
    this.gridApi = params.api;
    this.gridApi.hideOverlay();
    setTimeout(() => this.gridApi?.autoSizeAllColumns(), 100);
  }

  onExpandedGridReady(params: GridReadyEvent): void {
    this.expandedGridApi = params.api;
    this.expandedGridApi.hideOverlay();
    setTimeout(() => this.expandedGridApi?.autoSizeAllColumns(), 100);
  }

  refreshData(): void {
    this.gridApi?.hideOverlay();
  }

  /** Fetches expanded rows for the current demand-item version from the /items/expanded endpoint and filters to that item's Id. */
  private async loadExpandedItems(): Promise<void> {
    try {
      if (this.demandItemId) {
        this.expandedRowData = await this.apiService.getDemandItemExpandedByDemandItemId(this.demandItemId).toPromise() ?? [];
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      this.notificationService.error(`Failed to load expanded items: ${message}`);
      this.expandedRowData = [];
    }
  }

  /** Deep link to this page for a specific version id. */
  private buildDemandItemDetailsUrl(id: number | null | undefined): string | null {
    if (id == null) {
      return null;
    }
    return `/quota/capacity/demand-item/${id}`;
  }

  private buildGridOptions(): GridOptions<DemandItem> {
    return extendDefaultOptions({
      columnDefs: this.demandItemColumns(),
      paginationPageSize: this.initPageSize,
      rowSelection: "single",
      animateRows: true,
      getRowClass: (params) => (params.data?.Id === this.demandItemId ? "current-version-row" : ""),
    });
  }

  private buildExpandedGridOptions(): GridOptions<DemandItemExpanded> {
    return extendDefaultOptions({
      columnDefs: this.expandedColumns(),
      paginationPageSize: this.initPageSize,
      animateRows: true,
    });
  }

  private demandItemColumns(): ColDef[] {
    return [
      {
        headerName: "Id",
        field: "Id",
        filter: "agNumberColumnFilter",
        width: 100,
        cellRenderer: LinkCellComponent,
        cellRendererParams: {
          target: "_blank",
          url: (params: any) => this.buildDemandItemDetailsUrl(params.data?.Id),
        },
      },
      {
        headerName: "Demand Id",
        field: "DemandId",
        filter: "agNumberColumnFilter",
        cellRenderer: LinkCellComponent,
        cellRendererParams: {
          target: "_blank",
          url: (params: any) =>
            params.data?.DemandId != null ? `/quota/capacity/demand/${params.data.DemandId}` : null,
        },
      },
      { headerName: "Type", field: "Type", filter: "agTextColumnFilter" },
      { headerName: "SKU", field: "SKU", filter: "agTextColumnFilter" },
      { headerName: "Reserve", field: "ReserveCalculated", filter: "agNumberColumnFilter" },
      { headerName: "Limit", field: "LimitCalculated", filter: "agNumberColumnFilter" },
      { headerName: "Zone", field: "Zone", filter: "agTextColumnFilter" },
      { headerName: "Last Updated By", field: "LastUpdatedBy", filter: "agTextColumnFilter" },
      {
        headerName: "Last Updated Time (UTC)",
        field: "LastUpdatedTime",
        cellRenderer: (params: ICellRendererParams) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (a: any, b: any) => agDateColumnFilter(a, b),
        },
      },
    ];
  }

  private expandedColumns(): ColDef[] {
    return [
      { headerName: "Id", field: "Id", filter: "agNumberColumnFilter", width: 100 },
      { headerName: "Type", field: "Type", filter: "agTextColumnFilter", width: 120 },
      { headerName: "Name", field: "Name", filter: "agTextColumnFilter" },
      { headerName: "SKU", field: "SKU", filter: "agTextColumnFilter" },
      { headerName: "Reserve", field: "ReserveCalculated", filter: "agNumberColumnFilter", width: 100 },
      { headerName: "Limit", field: "LimitCalculated", filter: "agNumberColumnFilter", width: 90 },
      { headerName: "Zone", field: "Zone", filter: "agTextColumnFilter", width: 90 },
      {
        headerName: "Start Date",
        field: "StartDate",
        cellRenderer: (params: ICellRendererParams) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        width: 110,
      },
      {
        headerName: "End Date",
        field: "EndDate",
        cellRenderer: (params: ICellRendererParams) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        width: 110,
      },
    ];
  }
}
